/*
 * Данные кластера из его Kubernetes API (плагин Kubernetes, прокси бэкенда): узлы с
 * запросами подов и версия сервера. Загружаются один раз на страницу кластера и делятся
 * карточками «Узлы» и «О кластере» через контекст.
 */
import { createContext, ReactNode, useContext } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import useAsync from 'react-use/esm/useAsync';
import {
  K8sNode,
  K8sPod,
  NodeRow,
  requestsByNode,
  toNodeRow,
} from '../../../lib/k8s';

export type ClusterData = {
  /** Кластер описан в плагине Kubernetes; false — карточки объясняют, что подключить. */
  available: boolean;
  nodes: NodeRow[];
  /** Версия сервера API (gitVersion), например v1.33.3. */
  version?: string;
  /** Container runtime узлов, например containerd://2.2.1. */
  runtime?: string;
};

type State = { loading: boolean; error?: Error; value?: ClusterData };

const Ctx = createContext<State | undefined>(undefined);

export const ClusterDataProvider = ({ children }: { children: ReactNode }) => {
  const { entity } = useEntity();
  const kubernetesApi = useApi(kubernetesApiRef);
  const clusterName = entity.metadata.name;

  const state = useAsync(async (): Promise<ClusterData> => {
    const clusters = await kubernetesApi.getClusters();
    if (!clusters.some(c => c.name === clusterName)) {
      return { available: false, nodes: [] };
    }
    const get = async <T,>(path: string): Promise<T> => {
      const res = await kubernetesApi.proxy({ clusterName, path });
      if (!res.ok) {
        throw new Error(`${path}: HTTP ${res.status} ${await res.text()}`);
      }
      return res.json();
    };
    const [nodes, pods, version] = await Promise.all([
      get<{ items: K8sNode[] }>('/api/v1/nodes'),
      get<{ items: K8sPod[] }>(
        '/api/v1/pods?fieldSelector=status.phase!%3DSucceeded%2Cstatus.phase!%3DFailed',
      ),
      get<{ gitVersion?: string }>('/version').catch(
        (): { gitVersion?: string } => ({}),
      ),
    ]);
    const requests = requestsByNode(pods.items);
    return {
      available: true,
      nodes: nodes.items.map(n => toNodeRow(n, requests)),
      version: version.gitVersion,
      runtime: nodes.items[0]?.status?.nodeInfo?.containerRuntimeVersion,
    };
  }, [kubernetesApi, clusterName]);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
};

export const useClusterData = (): State => {
  const state = useContext(Ctx);
  if (!state) {
    throw new Error('useClusterData вне ClusterDataProvider');
  }
  return state;
};

/** Текст заглушки, когда кластер не описан в плагине Kubernetes. */
export const NOT_CONNECTED =
  'Данные читаются из API кластера через k8s-proxy GitLab Agent. Нужны user_access для группы viva в конфиге агента (шаблон k8s-cluster пишет его) и агент кластера в проекте viva/clusters — по нему модуль argocdInstances бэкенда описывает кластер для плагина Kubernetes.';
